/**
 * Which viewer event runs which action, and with what payload — declared by the
 * game's author, next to the actions themselves.
 *
 * `defineActions()` says what a game can be asked to do. It has never said *when*
 * or *how much*: a creator built that in the Bridge's rule builder, typing the
 * payload by hand, with the action's `maximum` as the only floor. An author who
 * declared `count` up to 5000 because a boss wave needs it had also, without
 * knowing, agreed that one rose could summon 5000 enemies.
 *
 * ```ts
 * export const ACTIONS = defineActions({ spawn_wave: { … }, invert_controls: { … } });
 *
 * export const BINDINGS = defineBindings(ACTIONS, {
 *   gift_small_wave: {
 *     label: '小ギフト → 敵の波(小)',
 *     description: '小ギフト1〜5個で、選んだ方向から同じ数だけ出ます。',
 *     trigger: { kind: 'GIFT', tier: 'SMALL', quantityBand: { min: 1, max: 5 } },
 *     action: { key: 'spawn_wave', payload: { count: 1, side: 'right' } },
 *     scaleWithQuantity: { field: 'count' },
 *     choices: [
 *       {
 *         choiceKey: 'enemy_side',
 *         label: '敵の出る方向',
 *         options: [
 *           { optionKey: 'right', label: '右から', patch: { side: 'right' } },
 *           { optionKey: 'left',  label: '左から', patch: { side: 'left' } },
 *         ],
 *         default: 'right',
 *       },
 *     ],
 *   },
 * });
 *
 * BINDINGS.manifestFragment();                          // write into manifest.json
 * BINDINGS.assertMatchesManifest(manifest.liveBindings); // and prove it stayed there
 * ```
 *
 * What a creator ever sends is a *key*: this binding on or off, this choice set
 * to that option. There is no numeric field in the document they submit, so
 * "the server does not read payload numbers from a creator's client" is a shape
 * rather than a promise. The numbers above are the author's, and stay the
 * author's for the life of the release.
 *
 * ## Type-level guarantees
 *
 * `action.key` is constrained to a key of the action set, and `payload` to that
 * action's own `PayloadOf`. A patch is a `Partial` of the same type, and
 * `scaleWithQuantity.field` is restricted to the numeric fields of it. So the
 * three ways a binding can be wrong about the action it drives are compile
 * errors in the author's editor, not `PAYLOAD_INVALID` acks on someone's stream.
 *
 * ## Runtime checks
 *
 * Everything docs/game-authored-action-bindings.md section 3.4 requires runs at
 * module load, here, in the author's own dev server. The platform's manifest
 * validator (`packages/game-manifest/src/live-bindings.ts`) enforces the same
 * rules at publication. The duplication is deliberate and is the same trade
 * `checkDeclaration` in `define-actions.ts` makes: this copy is the one that
 * gets read, that one is the one that is enforced. `binding-declaration-parity`
 * in `tests/contract` is what keeps them from drifting apart.
 */
import { canonicalJson } from './define-actions.js';
import { TRYPORTALError } from './errors.js';
import { compilePayloadSchema } from './payload-schema.js';
import { STANDARD_LIVE_ACTION_KEYS } from './stream-mode.js';
/* ------------------------------------------------------------ vocabulary -- */
export const GIFT_TIERS = ['SMALL', 'MEDIUM', 'LARGE', 'SUPREME'];
export const BINDING_TRIGGER_KINDS = [
    'GIFT',
    'COMMENT',
    'LIKE',
    'FOLLOW',
    'SHARE',
    'SUBSCRIPTION',
];
/* ------------------------------------------------------------- checking -- */
const KEY_PATTERN = /^[a-z][a-z0-9_]{1,63}$/;
const CHOICE_KEY_PATTERN = /^[a-z][a-z0-9_]{1,31}$/;
const GROUP_PATTERN = /^[a-z][a-z0-9_]{1,31}$/;
const SCALE_FIELD_PATTERN = /^[A-Za-z0-9_]{1,64}$/;
const KEYWORD_PATTERN = /^\S{1,32}$/;
const RESERVED_PREFIX = 'tp_live_';
const CANONICAL_RESERVED_KEYS = Object.values(STANDARD_LIVE_ACTION_KEYS);
const BODY_FIELDS = new Set([
    'label',
    'description',
    'trigger',
    'action',
    'scaleWithQuantity',
    'choices',
    'limits',
    'enabledByDefault',
    'exclusiveGroup',
]);
const TRIGGER_FIELDS = new Set(['kind', 'tier', 'quantityBand', 'keyword', 'likeThreshold']);
const CHOICE_FIELDS = new Set(['choiceKey', 'label', 'description', 'options', 'default']);
const OPTION_FIELDS = new Set(['optionKey', 'label', 'patch']);
const LIMIT_RANGES = [
    ['ttlMs', 250, 300_000],
    ['maxPerMinute', 1, 10_000],
    ['perActorCooldownMs', 0, 3_600_000],
    ['queueDepth', 1, 1000],
];
const OVERFLOW_POLICIES = new Set([
    'DROP_NEWEST',
    'DROP_OLDEST',
    'COALESCE',
    'REPLACE_LATEST',
    'PAUSE_RULE',
]);
function invalid(message) {
    throw new TRYPORTALError('INVALID_USAGE', message);
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function requireText(key, field, value, max) {
    if (typeof value !== 'string' || value.trim() === '' || value.length > max) {
        invalid(`binding '${key}' needs a ${field} of 1 to ${String(max)} characters`);
    }
    return value;
}
function requireInteger(key, field, value, min, max) {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) {
        invalid(`binding '${key}' needs ${field} as an integer between ${String(min)} and ${String(max)}, ` +
            `not ${JSON.stringify(value)}`);
    }
    return value;
}
/** The payload one selection produces: the author's literal, then the chosen patches. */
export function composeBindingPayload(base, patches) {
    const composed = { ...base };
    for (const patch of patches)
        Object.assign(composed, patch);
    return composed;
}
function checkTrigger(key, value) {
    if (!isRecord(value))
        invalid(`binding '${key}' needs a trigger object`);
    const stray = Object.keys(value).filter((field) => !TRIGGER_FIELDS.has(field));
    if (stray.length > 0) {
        invalid(`binding '${key}' trigger has field(s) the manifest does not define: ${stray.join(', ')}. ` +
            `Expected one of ${[...TRIGGER_FIELDS].join(', ')}.`);
    }
    const kind = value['kind'];
    if (typeof kind !== 'string' || !BINDING_TRIGGER_KINDS.includes(kind)) {
        invalid(`binding '${key}' needs trigger.kind of ${BINDING_TRIGGER_KINDS.join(', ')}, ` +
            `not ${JSON.stringify(kind)}`);
    }
    const tier = value['tier'];
    if (tier !== undefined) {
        if (kind !== 'GIFT')
            invalid(`binding '${key}': only a GIFT trigger has a tier`);
        if (typeof tier !== 'string' || !GIFT_TIERS.includes(tier)) {
            invalid(`binding '${key}' needs trigger.tier of ${GIFT_TIERS.join(', ')}, ` +
                `not ${JSON.stringify(tier)}. The vocabulary is fixed: which concrete gift is which ` +
                'tier is platform data, not a manifest value.');
        }
    }
    const band = value['quantityBand'];
    if (band !== undefined) {
        if (kind !== 'GIFT')
            invalid(`binding '${key}': only a GIFT trigger has a quantityBand`);
        if (!isRecord(band))
            invalid(`binding '${key}' quantityBand must be an object`);
        const stray2 = Object.keys(band).filter((field) => field !== 'min' && field !== 'max');
        if (stray2.length > 0) {
            invalid(`binding '${key}' quantityBand has unknown field(s): ${stray2.join(', ')}`);
        }
        const min = requireInteger(key, 'quantityBand.min', band['min'], 1, 1_000_000);
        if (band['max'] !== undefined) {
            const max = requireInteger(key, 'quantityBand.max', band['max'], 1, 1_000_000);
            if (max < min) {
                invalid(`binding '${key}' has quantityBand max ${String(max)} below min ${String(min)}, ` +
                    'so it matches nothing');
            }
        }
    }
    const keyword = value['keyword'];
    if (keyword !== undefined) {
        if (kind !== 'COMMENT')
            invalid(`binding '${key}': only a COMMENT trigger has a keyword`);
        if (typeof keyword !== 'string' || !KEYWORD_PATTERN.test(keyword)) {
            invalid(`binding '${key}' needs a keyword of 1 to 32 non-space characters`);
        }
    }
    else if (kind === 'COMMENT') {
        invalid(`binding '${key}' is a COMMENT trigger with no keyword, so it would fire on every comment ` +
            'on the stream');
    }
    const likeThreshold = value['likeThreshold'];
    if (likeThreshold !== undefined) {
        if (kind !== 'LIKE')
            invalid(`binding '${key}': only a LIKE trigger has a likeThreshold`);
        requireInteger(key, 'likeThreshold', likeThreshold, 1, 1_000_000_000);
    }
    else if (kind === 'LIKE') {
        invalid(`binding '${key}' is a LIKE trigger with no likeThreshold`);
    }
    return value;
}
function checkChoices(key, value) {
    if (value === undefined)
        return [];
    if (!Array.isArray(value))
        invalid(`binding '${key}' choices must be an array`);
    if (value.length > 5)
        invalid(`binding '${key}' declares more than 5 choices`);
    const seenChoiceKeys = new Set();
    const fieldOwner = new Map();
    for (const choice of value) {
        if (!isRecord(choice))
            invalid(`binding '${key}' has a choice that is not an object`);
        const stray = Object.keys(choice).filter((field) => !CHOICE_FIELDS.has(field));
        if (stray.length > 0) {
            invalid(`binding '${key}' choice has unknown field(s): ${stray.join(', ')}`);
        }
        const choiceKey = choice['choiceKey'];
        if (typeof choiceKey !== 'string' || !CHOICE_KEY_PATTERN.test(choiceKey)) {
            invalid(`binding '${key}' has a choiceKey that does not match ^[a-z][a-z0-9_]{1,31}$: ` +
                JSON.stringify(choiceKey));
        }
        if (seenChoiceKeys.has(choiceKey)) {
            invalid(`binding '${key}' declares choice '${choiceKey}' twice. A profile stores one option per ` +
                'choiceKey, so one of them could never be selected.');
        }
        seenChoiceKeys.add(choiceKey);
        requireText(key, `choices['${choiceKey}'].label`, choice['label'], 60);
        const description = choice['description'];
        if (description !== undefined && (typeof description !== 'string' || description.length > 200)) {
            invalid(`binding '${key}' choice '${choiceKey}' description must be at most 200 characters`);
        }
        const options = choice['options'];
        if (!Array.isArray(options) || options.length < 2 || options.length > 12) {
            invalid(`binding '${key}' choice '${choiceKey}' needs between 2 and 12 options. A choice with one ` +
                'option is a value the author should have written into the payload.');
        }
        const seenOptionKeys = new Set();
        const touched = new Set();
        for (const option of options) {
            if (!isRecord(option))
                invalid(`binding '${key}' choice '${choiceKey}' has a bad option`);
            const strayOption = Object.keys(option).filter((field) => !OPTION_FIELDS.has(field));
            if (strayOption.length > 0) {
                invalid(`binding '${key}' choice '${choiceKey}' option has unknown field(s): ` +
                    strayOption.join(', '));
            }
            const optionKey = option['optionKey'];
            if (typeof optionKey !== 'string' || !CHOICE_KEY_PATTERN.test(optionKey)) {
                invalid(`binding '${key}' choice '${choiceKey}' has an optionKey that does not match ` +
                    `^[a-z][a-z0-9_]{1,31}$: ${JSON.stringify(optionKey)}`);
            }
            if (seenOptionKeys.has(optionKey)) {
                invalid(`binding '${key}' choice '${choiceKey}' declares option '${optionKey}' twice`);
            }
            seenOptionKeys.add(optionKey);
            requireText(key, `choices['${choiceKey}'].options['${optionKey}'].label`, option['label'], 60);
            const patch = option['patch'];
            if (!isRecord(patch) || Object.keys(patch).length === 0) {
                invalid(`binding '${key}' choice '${choiceKey}' option '${optionKey}' needs a patch with at ` +
                    'least one field. An option that changes nothing is a label with no meaning.');
            }
            for (const field of Object.keys(patch))
                touched.add(field);
        }
        const fallback = choice['default'];
        if (typeof fallback !== 'string' || !seenOptionKeys.has(fallback)) {
            invalid(`binding '${key}' choice '${choiceKey}' has default ${JSON.stringify(fallback)}, which is ` +
                `not one of its options (${[...seenOptionKeys].join(', ')}). The default is what a ` +
                'creator who never opens the setting gets.');
        }
        for (const field of touched) {
            const owner = fieldOwner.get(field);
            if (owner !== undefined) {
                invalid(`binding '${key}': choices '${owner}' and '${choiceKey}' both patch payload field ` +
                    `'${field}'. Which one wins would depend on merge order. Give each choice its own ` +
                    'fields — that is what makes "a field no choice mentions is fixed" true.');
            }
            fieldOwner.set(field, choiceKey);
        }
    }
    return value;
}
function checkLimits(key, value) {
    if (value === undefined)
        return;
    if (!isRecord(value))
        invalid(`binding '${key}' limits must be an object`);
    const known = new Set([...LIMIT_RANGES.map(([field]) => field), 'overflowPolicy']);
    const stray = Object.keys(value).filter((field) => !known.has(field));
    if (stray.length > 0) {
        invalid(`binding '${key}' limits has unknown field(s): ${stray.join(', ')}`);
    }
    for (const [field, min, max] of LIMIT_RANGES) {
        if (value[field] !== undefined)
            requireInteger(key, `limits.${field}`, value[field], min, max);
    }
    const policy = value['overflowPolicy'];
    if (policy !== undefined && (typeof policy !== 'string' || !OVERFLOW_POLICIES.has(policy))) {
        invalid(`binding '${key}' needs a limits.overflowPolicy of ${[...OVERFLOW_POLICIES].join(', ')}`);
    }
}
/** `[min, max]` on quantityDelta; an absent band means any quantity. */
function bandOf(trigger) {
    return {
        min: trigger.quantityBand?.min ?? 1,
        max: trigger.quantityBand?.max ?? Number.POSITIVE_INFINITY,
    };
}
function describeBand(trigger) {
    const { min, max } = bandOf(trigger);
    return `${trigger.tier ?? 'any tier'} ${max === Number.POSITIVE_INFINITY ? `${String(min)}+` : `${String(min)}-${String(max)}`}`;
}
function checkOneDeclaration(key, body, actions) {
    if (!KEY_PATTERN.test(key)) {
        invalid(`binding key '${key}' does not match ^[a-z][a-z0-9_]{1,63}$. Binding keys use underscores, ` +
            'like action keys.');
    }
    if (!isRecord(body))
        invalid(`binding '${key}' must be an object`);
    const stray = Object.keys(body).filter((field) => !BODY_FIELDS.has(field));
    if (stray.length > 0) {
        invalid(`binding '${key}' has field(s) the manifest does not define: ${stray.join(', ')}. ` +
            `Expected one of ${[...BODY_FIELDS].join(', ')}.`);
    }
    requireText(key, 'label', body['label'], 60);
    requireText(key, 'description', body['description'], 400);
    const trigger = checkTrigger(key, body['trigger']);
    const action = body['action'];
    if (!isRecord(action))
        invalid(`binding '${key}' needs an action object`);
    const strayAction = Object.keys(action).filter((field) => field !== 'key' && field !== 'payload');
    if (strayAction.length > 0) {
        invalid(`binding '${key}' action has unknown field(s): ${strayAction.join(', ')}`);
    }
    const actionKey = action['key'];
    if (typeof actionKey !== 'string')
        invalid(`binding '${key}' needs a string action.key`);
    if (actionKey.startsWith(RESERVED_PREFIX) || CANONICAL_RESERVED_KEYS.includes(actionKey)) {
        invalid(`binding '${key}' binds '${actionKey}', which is in the platform-reserved ` +
            `${RESERVED_PREFIX}* namespace. Those are the platform's own delivery of viewer chat, ` +
            'gifts and follows; enableStreamMode() turns them on and no binding fires them.');
    }
    if (!actions.has(actionKey)) {
        invalid(`binding '${key}' binds '${actionKey}', which these actions do not declare. ` +
            `Declared: ${actions.keys.join(', ')}.`);
    }
    const payload = action['payload'];
    if (!isRecord(payload)) {
        invalid(`binding '${key}' needs an action.payload object. It is a JSON literal — there is no ` +
            'template syntax here, and the one event-dependent value comes from scaleWithQuantity.');
    }
    const choices = checkChoices(key, body['choices']);
    checkLimits(key, body['limits']);
    const enabledByDefault = body['enabledByDefault'];
    if (enabledByDefault !== undefined && typeof enabledByDefault !== 'boolean') {
        invalid(`binding '${key}' enabledByDefault must be a boolean`);
    }
    const group = body['exclusiveGroup'];
    if (group !== undefined && (typeof group !== 'string' || !GROUP_PATTERN.test(group))) {
        invalid(`binding '${key}' exclusiveGroup does not match ^[a-z][a-z0-9_]{1,31}$: ` +
            JSON.stringify(group));
    }
    /* ------------------------------------------------------ scale with quantity -- */
    const declaration = actions.get(actionKey);
    const schema = declaration.payloadSchema;
    const scale = body['scaleWithQuantity'];
    const patchedFields = new Set();
    for (const choice of choices) {
        for (const option of choice.options) {
            for (const field of Object.keys(option.patch))
                patchedFields.add(field);
        }
    }
    if (scale !== undefined) {
        if (!isRecord(scale) || Object.keys(scale).some((field) => field !== 'field')) {
            invalid(`binding '${key}' scaleWithQuantity takes exactly one field: 'field'`);
        }
        const field = scale['field'];
        if (typeof field !== 'string' || !SCALE_FIELD_PATTERN.test(field)) {
            invalid(`binding '${key}' scaleWithQuantity.field does not match ^[A-Za-z0-9_]{1,64}$: ` +
                JSON.stringify(field));
        }
        if (trigger.kind !== 'GIFT') {
            invalid(`binding '${key}' scales with quantity but triggers on ${trigger.kind}. Only a gift ` +
                'carries payload.quantityDelta.');
        }
        const band = trigger.quantityBand;
        if (band === undefined) {
            invalid(`binding '${key}' scales '${field}' with quantity but declares no quantityBand. Without ` +
                'one, a large gift renders a payload the action schema rejects, and a rejected payload ' +
                'is dropped at delivery rather than clamped.');
        }
        if (patchedFields.has(field)) {
            invalid(`binding '${key}' scales '${field}' with quantity and also lets a choice patch it. The ` +
                'compiler writes one value into that field; two authors for it is one too many.');
        }
        const properties = isRecord(schema['properties']) ? schema['properties'] : {};
        const property = properties[field];
        if (!isRecord(property) ||
            (property['type'] !== 'integer' && property['type'] !== 'number')) {
            invalid(`binding '${key}' scales '${field}', which is not a numeric property of ` +
                `liveActions['${actionKey}'].payloadSchema`);
        }
        const minimum = property['minimum'];
        const maximum = property['maximum'];
        if (typeof minimum === 'number' && band.min < minimum) {
            invalid(`binding '${key}' has a band starting at ${String(band.min)} but '${field}' has minimum ` +
                `${String(minimum)}; the smallest gift in the band would render a payload the action ` +
                'rejects');
        }
        if (band.max === undefined) {
            invalid(`binding '${key}' scales '${field}' with an open-ended band. An open band has no largest ` +
                'gift, so no maximum can be proven against the action schema. Close the band, or drop ' +
                'scaleWithQuantity and send a literal.');
        }
        if (typeof maximum === 'number' && band.max > maximum) {
            invalid(`binding '${key}' has a band reaching ${String(band.max)} but '${field}' has maximum ` +
                `${String(maximum)}; gifts above ${String(maximum)} would render a payload the action ` +
                'rejects and the delivery would be dropped, not clamped.');
        }
    }
    /* -------------------------------------------------------- composed payloads -- */
    // Throws here, at import, if the action's own schema is outside the subset —
    // the same wall `defineActions` puts up, reached from the other side.
    const validate = compilePayloadSchema(schema);
    const defaults = choices.map((choice) => (choice.options.find((option) => option.optionKey === choice.default)?.patch ??
        {}));
    const candidates = [
        { label: 'the default selection', payload: composeBindingPayload(payload, defaults) },
    ];
    choices.forEach((choice, index) => {
        const others = defaults.filter((_, otherIndex) => otherIndex !== index);
        for (const option of choice.options) {
            candidates.push({
                label: `${choice.choiceKey} = ${option.optionKey}`,
                payload: composeBindingPayload(payload, [
                    ...others,
                    option.patch,
                ]),
            });
        }
    });
    for (const candidate of candidates) {
        if (validate(candidate.payload))
            continue;
        invalid(`binding '${key}' with ${candidate.label} composes ${JSON.stringify(candidate.payload)}, ` +
            `which liveActions['${actionKey}'].payloadSchema rejects. Every option a creator can ` +
            'pick has to produce a payload the action accepts, or the delivery is dropped with ' +
            'PAYLOAD_INVALID and nothing on stream explains why.');
    }
    // `key` first, so `manifestFragment()` writes an entry that reads the way the
    // hand-written `liveActions` entries next to it do.
    return { key, ...body };
}
/* ------------------------------------------------------------ the set -- */
/**
 * Declare a game's bindings once, against the actions they drive.
 *
 * ```ts
 * export const BINDINGS = defineBindings(ACTIONS, { gift_small_wave: { … } });
 * ```
 *
 * **Deliberately not an overloaded function.** `defineActions` accepts either a
 * record or `manifest.liveActions`, and the second signature costs nothing there
 * because nothing about an action's declaration is checked against another type.
 * Here everything is: `action.key` against the action set, `payload` against
 * that action's own schema, a patch against the same. When a second overload
 * exists, a literal that violates any of those produces
 * `TS2769: No overload matches this call` pointing at the *call* — every
 * `@ts-expect-error` written next to the offending line becomes an unused
 * directive, so a test asserting the refusal starts failing for looking stale
 * while the refusal itself still works, and an author gets a wall of text
 * instead of "'durationMs' does not exist in type". One signature puts the error
 * on the binding that is wrong. The JSON-import path is
 * `defineBindingsFromManifest` below.
 */
export function defineBindings(actions, 
/*
 * `B & Record<…>` rather than plain `B`, so the literal is checked *as a
 * literal* against the body type. With plain `B` the inferred type simply
 * absorbs any extra field and the constraint check compares two types, where
 * excess properties are legal — `priority: 10` next to `label` compiled, and
 * publication then refused the release for `additionalProperties: false` on a
 * field the author's editor had accepted.
 */
declarations) {
    return build(actions, declarations);
}
/**
 * The same checks, driven from `manifest.liveBindings` after a JSON import.
 *
 * A JSON import has already widened every literal, so no payload type can be
 * recovered from it and none is claimed: this returns an untyped set. It exists
 * so that a game keeping `manifest.json` as its source of truth, and any tool
 * that has a manifest but no declarations, still gets every runtime check.
 */
export function defineBindingsFromManifest(actions, manifestLiveBindings) {
    if (!Array.isArray(manifestLiveBindings)) {
        invalid('defineBindingsFromManifest() needs the manifest liveBindings array');
    }
    return build(actions, manifestLiveBindings);
}
function build(actions, declarations) {
    const set = actions;
    if (typeof set.has !== 'function' || typeof set.get !== 'function') {
        invalid('defineBindings() takes the action set as its first argument: ' +
            'defineBindings(ACTIONS, { … }). Bindings are checked against the actions they drive.');
    }
    const pairs = Array.isArray(declarations)
        ? declarations.map((entry) => {
            if (!isRecord(entry) || typeof entry['key'] !== 'string') {
                invalid('every entry of a liveBindings array needs a string key');
            }
            const { key, ...body } = entry;
            return [key, body];
        })
        : Object.entries(declarations);
    const ordered = [];
    const entries = new Map();
    for (const [key, body] of pairs) {
        if (entries.has(key))
            invalid(`binding '${key}' is declared more than once`);
        const entry = checkOneDeclaration(key, body, set);
        entries.set(key, entry);
        ordered.push(entry);
    }
    /*
     * Trigger exclusivity, across the set rather than within one binding.
     *
     * Two bands that overlap both fire for the same gift. The author designed one
     * effect and gets two, and the arithmetic is invisible: a 3-gift matching
     * "1–5" and "3+" spawns 3 + 5 = 8 enemies, in a game whose author capped the
     * wave at 5. There is no runtime signal for it either — both deliveries are
     * valid, acked, and correct according to every other check in the system.
     */
    const groups = new Map();
    for (const entry of ordered) {
        if (entry.trigger.kind !== 'GIFT')
            continue;
        const group = entry.exclusiveGroup ?? entry.trigger.kind;
        const peers = groups.get(group) ?? [];
        for (const peer of peers) {
            const peerTiers = peer.trigger.tier === undefined ? new Set(GIFT_TIERS) : new Set([peer.trigger.tier]);
            const mineTiers = entry.trigger.tier === undefined ? GIFT_TIERS : [entry.trigger.tier];
            if (!mineTiers.some((tier) => peerTiers.has(tier)))
                continue;
            const mine = bandOf(entry.trigger);
            const theirs = bandOf(peer.trigger);
            if (mine.min > theirs.max || theirs.min > mine.max)
                continue;
            invalid(`bindings '${peer.key}' (${describeBand(peer.trigger)}) and '${entry.key}' ` +
                `(${describeBand(entry.trigger)}) both match the same gift, so one gift runs both ` +
                'actions. Split the tiers, make the bands disjoint, or put them in different ' +
                'exclusiveGroups if firing together is what you meant.');
        }
        peers.push(entry);
        groups.set(group, peers);
    }
    const keys = ordered.map((entry) => entry.key);
    const clone = (entry) => structuredClone(entry);
    return {
        keys,
        has(key) {
            return entries.has(key);
        },
        get(key) {
            const entry = entries.get(key);
            if (entry === undefined) {
                invalid(`no binding '${key}' is declared. Declared: ${keys.join(', ')}`);
            }
            return clone(entry);
        },
        manifestFragment() {
            return ordered.map(clone);
        },
        assertMatchesManifest(manifestLiveBindings) {
            if (!Array.isArray(manifestLiveBindings)) {
                invalid('assertMatchesManifest() needs the manifest liveBindings array. A manifest with no ' +
                    'liveBindings is a release where these bindings do not exist: write ' +
                    'manifestFragment() into manifest.json.');
            }
            const fromManifest = new Map();
            for (const entry of manifestLiveBindings) {
                if (!isRecord(entry) || typeof entry['key'] !== 'string') {
                    invalid('every entry of manifest.liveBindings needs a string key');
                }
                fromManifest.set(entry['key'], entry);
            }
            const absent = keys.filter((key) => !fromManifest.has(key));
            if (absent.length > 0) {
                invalid(`manifest.json does not declare binding(s): ${absent.join(', ')}. The platform reads ` +
                    'manifest.json and nothing else, so these bindings do not exist in production. ' +
                    'Write manifestFragment() into it.');
            }
            const extra = [...fromManifest.keys()].filter((key) => !entries.has(key));
            if (extra.length > 0) {
                invalid(`manifest.json declares binding(s) these declarations do not: ${extra.join(', ')}. ` +
                    'Those will be compiled into rules and fire on stream, with a payload nothing in ' +
                    'this file has reasoned about.');
            }
            const drifted = ordered.filter((entry) => canonicalJson(entry) !== canonicalJson(fromManifest.get(entry.key)));
            if (drifted.length > 0) {
                invalid('manifest.json disagrees with these declarations for binding(s): ' +
                    `${drifted.map((entry) => entry.key).join(', ')}. ` +
                    'Write manifestFragment() into manifest.json before publishing.');
            }
        },
    };
}
